import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { Modal, ModalConfirmFooter } from '../components/Modal.jsx';
import { PageWelcome } from '../components/PageWelcome.jsx';
import { projectTypeLabel } from '../components/ProjectTypeField.jsx';
import { DEFAULT_TASK_COLUMNS, stageColor, stageInk, stageWash } from '../utils/taskProgress.js';

const STATUS_LABELS = {
  draft: 'Draft',
  active: 'Active',
  on_hold: 'On hold',
  done: 'Done',
  cancelled: 'Cancelled',
};

export function AdminProjectTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => apiJson('/api/admin/project-templates').then(setTemplates);

  useEffect(() => {
    Promise.all([
      load(),
      apiJson('/api/admin/project-types').then(setTypes).catch(() => setTypes([])),
    ])
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const onDelete = async () => {
    if (!templateToDelete) return;
    setDeleting(true);
    setError('');
    try {
      await apiJson(`/api/admin/project-templates/${templateToDelete.id}`, { method: 'DELETE' });
      setTemplateToDelete(null);
      await load();
    } catch (err) {
      setError(err.message);
      setTemplateToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageWelcome
        title="Project templates"
        subtitle="Save a project type — stages, progress, and starter tasks — and start the next one already laid out."
        actions={
          <>
            <Link to="/admin/projects" className="btn-secondary">
              Projects
            </Link>
            <Link to="/admin/project-templates/new" className="btn-primary">
              + New template
            </Link>
          </>
        }
      />
      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="err">{error}</p> : null}

      {!loading && templates.length === 0 ? (
        <div className="tpl-panel">
          <p style={{ marginTop: 0 }}>No templates yet.</p>
          <p className="muted">
            Create a template for a repeating engagement, or start a custom project when none of them fit.
          </p>
          <Link to="/admin/project-templates/new" className="btn-primary">
            + New template
          </Link>
        </div>
      ) : null}

      <div className="template-catalog">
        {templates.map((template) => {
          const storedStages = template.stages || [];
          const stages = storedStages.length ? storedStages : DEFAULT_TASK_COLUMNS;
          const tasks = template.tasks || [];
          const visibleStages = stages.slice(0, 4);
          return (
            <article key={template.id} className="template-card">
              <Link to={`/admin/project-templates/${template.id}`} className="template-card-link">
                <span className="template-card-kicker">
                  {projectTypeLabel(types, template.type)}
                  {' · '}
                  {STATUS_LABELS[template.status] || template.status || 'Draft'}
                </span>
                <strong>{template.name}</strong>
                <p className="muted small">{template.description || 'No description'}</p>
                {visibleStages.length ? (
                  <p className="template-card-flow">
                    {visibleStages.map((stage, index) => {
                      const color = stageColor(stage, index);
                      return (
                        <span
                          key={stage.key}
                          className="template-chip"
                          style={{ color: stageInk(color), background: stageWash(color, 0.88) }}
                        >
                          {stage.name || stage.key}
                        </span>
                      );
                    })}
                    {stages.length > visibleStages.length ? (
                      <span className="template-chip">+{stages.length - visibleStages.length}</span>
                    ) : null}
                  </p>
                ) : null}
                <span className="muted small">
                  {storedStages.length ? `${storedStages.length} stage${storedStages.length === 1 ? '' : 's'}` : 'Default stages'}
                  {' · '}
                  {tasks.length} starter task{tasks.length === 1 ? '' : 's'}
                </span>
              </Link>
              <div className="row-actions">
                <Link to={`/admin/project-templates/${template.id}`} className="btn-link">
                  Edit
                </Link>
                <button type="button" className="btn-link" onClick={() => setTemplateToDelete(template)}>
                  Delete
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <Modal
        open={Boolean(templateToDelete)}
        title={templateToDelete ? `Delete “${templateToDelete.name}”?` : 'Delete template'}
        description="Existing projects created from this template stay as they are."
        onClose={() => {
          if (!deleting) setTemplateToDelete(null);
        }}
        footer={
          <ModalConfirmFooter
            danger
            confirmLabel="Delete"
            confirmBusy={deleting}
            onCancel={() => setTemplateToDelete(null)}
            onConfirm={() => void onDelete()}
          />
        }
      >
        <p className="muted small" style={{ margin: 0 }}>
          This only removes the template.
        </p>
      </Modal>
    </div>
  );
}
